<template>
  <div class="social-proof-dialog">
    <v-form ref="form" v-model="isFormValid">
      <v-row>
        <v-col cols="12">
          <v-select
            v-model="formData.starRating"
            :items="starRatingOptions"
            label="Star rating"
            :rules="[v => !!v || 'Star rating is required']"
            persistent-placeholder />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="formData.reviewText"
            label="Review text"
            placeholder="Enter review text"
            :rules="[v => !!v || 'Review text is required']"
            rows="3"
            persistent-placeholder />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model="formData.name"
            label="Name"
            placeholder="Enter reviewer name"
            :rules="[v => !!v || 'Name is required']"
            persistent-placeholder />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <VaImageDragAndDropInput
            :label="'Photo'"
            :item-id="(formData.photo && formData.photo.id) || ''"
            @update:photo="updatePhoto" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <VaColorPicker
            name="photoOutline"
            :position="'top'"
            :title="'Outline of the photo'"
            v-model="formData.photoOutline"></VaColorPicker>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <VaColorPicker
            name="backgroundColor"
            :position="'top'"
            :title="'Background color'"
            v-model="formData.backgroundColor"></VaColorPicker>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <VaColorPicker
            name="textColor"
            :position="'top'"
            :title="'Text color'"
            v-model="formData.textColor"></VaColorPicker>
        </v-col>
      </v-row>

      <v-alert v-if="errorMessage" type="error" class="mb-4" dismissible @input="errorMessage = ''">
        {{ errorMessage }}
      </v-alert>
    </v-form>
  </div>
</template>

<script>
  import definedAbility from "@/ability";
  import VaColorPicker from "@/components/custom/VaColorPicker.vue";

  export default {
    name: "SocialProofDialog",
    components: {
      VaColorPicker,
    },
    data() {
      return {
        isFormValid: false,
        ability: definedAbility(),
        formData: {
          starRating: 5,
          reviewText: "",
          name: "",
          photo: null,
          photoOutline: "#37A9FC",
          backgroundColor: "#F0F9FF",
          textColor: "#1C1C1C",
        },
        starRatingOptions: [1, 2, 3, 4, 5],
        errorMessage: "",
        SOCIAL_PROOF_EVENT_TYPES: {
          REQUEST_DATA: "requestData",
          UPDATE_SOCIAL_PROOF_DATA: "updateSocialProofData",
          VALIDATION: "validation",
          NEED_VALIDATION: "needValidation",
          SEND_DATA: "sendData",
        },
      };
    },
    watch: {
      isFormValid: {
        handler() {
          this.sendValidation();
        },
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    methods: {
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (message.data.type === this.SOCIAL_PROOF_EVENT_TYPES.NEED_VALIDATION) {
            this.$refs.form.validate();
          }
          if (message.data.type === this.SOCIAL_PROOF_EVENT_TYPES.REQUEST_DATA) {
            this.sendData();
          }
          if (message.data.type === this.SOCIAL_PROOF_EVENT_TYPES.SEND_DATA) {
            const { data } = message.data;
            if (data === "undefined") {
              // Handle case where user exits without saving
              this.updateValue();
            } else {
              this.formData = {
                starRating: data.starRating || 5,
                reviewText: data.reviewText || "",
                name: data.name || "",
                photo: data.photo || null,
                photoOutline: data.photoOutline || "#37A9FC",
                backgroundColor: data.backgroundColor || "#F0F9FF",
                textColor: data.textColor || "#1C1C1C",
              };
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: this.SOCIAL_PROOF_EVENT_TYPES.REQUEST_DATA,
          },
          "*",
        );
      },
      sendData() {
        window.parent.postMessage(
          {
            mceAction: this.SOCIAL_PROOF_EVENT_TYPES.SEND_DATA,
            data: this.formData,
          },
          "*",
        );
      },
      async validateAndUpdate() {
        if (this.$refs.form) {
          await this.$refs.form.validate();
          if (this.isFormValid) {
            this.updateValue();
            this.errorMessage = "";
          }
        }
      },
      updateValue() {
        window.parent.postMessage(
          {
            mceAction: this.SOCIAL_PROOF_EVENT_TYPES.UPDATE_SOCIAL_PROOF_DATA,
            data: this.formData,
          },
          "*",
        );
      },
      sendValidation() {
        window.parent.postMessage(
          {
            mceAction: this.SOCIAL_PROOF_EVENT_TYPES.VALIDATION,
            data: {
              isValid: this.isFormValid,
            },
          },
          "*",
        );
      },
      updatePhoto(photoData) {
        if (photoData && photoData !== "") {
          this.formData.photo = {
            id: photoData["@id"],
            contentUrl: photoData.contentUrl,
          };
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .social-proof-dialog {
    padding: 20px;
  }
</style>
